/**
 * Client List Component
 * Displays list of clients in grid or list view
 */

import React, { useState } from 'react';
import { formatCurrency } from '../utils/formatters';
import { useClientStore } from '../store/clientStore';
import './ClientList.css';

// Portal Access Modal Component
const PortalAccessModal = ({ client, onClose }) => {
  const [portalData, setPortalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const { createPortalAccess, getPortalAccess, disablePortalAccess, regeneratePortalToken } = useClientStore();

  // Load portal access status on mount
  React.useEffect(() => {
    loadPortalStatus();
  }, [client.id]);

  const loadPortalStatus = async () => {
    try {
      setLoading(true);
      const data = await getPortalAccess(client.id);
      setPortalData(data);
    } catch (error) {
      console.error('Error loading portal status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEnablePortal = async () => {
    try {
      setActionLoading(true);
      const result = await createPortalAccess(client.id);
      setPortalData({
        hasPortalAccess: true,
        portalEnabled: true,
        accessToken: result.accessToken,
        portalUrl: result.portalUrl,
        permissions: {
          canViewInvoices: true,
          canDownloadPdf: true,
          canUpdatePayment: true,
        },
      });
    } catch (error) {
      alert('Error enabling portal: ' + error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleDisablePortal = async () => {
    if (!window.confirm('Are you sure you want to disable portal access for this client?')) {
      return;
    }
    try {
      setActionLoading(true);
      await disablePortalAccess(client.id);
      setPortalData(prev => ({ ...prev, portalEnabled: false }));
    } catch (error) {
      alert('Error disabling portal: ' + error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRegenerateToken = async () => {
    if (!window.confirm('This will invalidate the current portal URL. The client will need the new URL to access their portal. Continue?')) {
      return;
    }
    try {
      setActionLoading(true);
      const result = await regeneratePortalToken(client.id);
      setPortalData(prev => ({
        ...prev,
        accessToken: result.accessToken,
        portalUrl: result.portalUrl,
      }));
    } catch (error) {
      alert('Error regenerating token: ' + error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(portalData.portalUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content portal-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Portal Access: {client.name}</h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>Loading...</div>
          ) : (
            <>
              <div className="portal-status">
                <strong>Status:</strong>{' '}
                {portalData?.portalEnabled ? (
                  <span style={{ color: '#22c55e' }}>Enabled</span>
                ) : portalData?.hasPortalAccess ? (
                  <span style={{ color: '#f59e0b' }}>Disabled</span>
                ) : (
                  <span style={{ color: '#6b7280' }}>Not Set Up</span>
                )}
              </div>

              {portalData?.portalEnabled && portalData?.portalUrl && (
                <div className="portal-url-section" style={{ marginTop: '1rem' }}>
                  <label style={{ fontWeight: '600', display: 'block', marginBottom: '0.5rem' }}>
                    Portal URL:
                  </label>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <input
                      type="text"
                      readOnly
                      value={portalData.portalUrl}
                      style={{
                        flex: 1,
                        padding: '0.5rem',
                        borderRadius: '4px',
                        border: '1px solid #d1d5db',
                        fontSize: '0.875rem',
                      }}
                    />
                    <button
                      className="btn btn-secondary"
                      onClick={handleCopyUrl}
                      style={{ whiteSpace: 'nowrap' }}
                    >
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                    <a
                      href={portalData.portalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary"
                      style={{ textDecoration: 'none' }}
                    >
                      Open
                    </a>
                  </div>
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
                    Share this URL with your client so they can view invoices and manage payments.
                  </p>
                </div>
              )}

              {portalData?.hasPortalAccess && portalData?.accessCount > 0 && (
                <div style={{ marginTop: '1rem', fontSize: '0.875rem', color: '#6b7280' }}>
                  <p>Portal accessed {portalData.accessCount} time(s)</p>
                  {portalData.lastAccessedAt && (
                    <p>Last access: {new Date(portalData.lastAccessedAt._seconds * 1000).toLocaleString()}</p>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-actions">
          {!loading && (
            <>
              {!portalData?.hasPortalAccess || !portalData?.portalEnabled ? (
                <button
                  className="btn btn-primary"
                  onClick={handleEnablePortal}
                  disabled={actionLoading}
                >
                  {actionLoading ? 'Enabling...' : 'Enable Portal Access'}
                </button>
              ) : (
                <>
                  <button
                    className="btn btn-secondary"
                    onClick={handleRegenerateToken}
                    disabled={actionLoading}
                    title="Generate a new portal URL"
                  >
                    Regenerate URL
                  </button>
                  <button
                    className="btn"
                    style={{ backgroundColor: '#dc2626', color: 'white' }}
                    onClick={handleDisablePortal}
                    disabled={actionLoading}
                  >
                    Disable Portal
                  </button>
                </>
              )}
              <button className="btn btn-secondary" onClick={onClose}>
                Close
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const ClientList = ({ clients, onEdit, onDelete, viewMode = 'grid' }) => {
  const [portalModalClient, setPortalModalClient] = useState(null);
  if (clients.length === 0) {
    return (
      <div className="empty-state">
        <p>No clients yet. Add your first client to get started!</p>
      </div>
    );
  }

  if (viewMode === 'list') {
    return (
      <div className="client-table-container">
        <table className="client-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Company</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Default Rate</th>
              <th>Notes</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {clients.map(client => (
              <tr key={client.id}>
                <td className="client-table-name">{client.name}</td>
                <td>{client.company || '-'}</td>
                <td>{client.email}</td>
                <td>{client.phone || '-'}</td>
                <td className="client-rate">
                  {formatCurrency(client.defaultRate, client.currency)}/hr
                </td>
                <td className="client-table-notes" title={client.notes}>
                  {client.notes ? (client.notes.length > 40 ? client.notes.substring(0, 40) + '...' : client.notes) : '-'}
                </td>
                <td>
                  <div className="client-actions">
                    <button
                      className="btn-icon"
                      onClick={() => setPortalModalClient(client)}
                      title="Portal Access"
                    >
                      🌐
                    </button>
                    <button
                      className="btn-icon"
                      onClick={() => onEdit(client)}
                      title="Edit"
                    >
                      ✏️
                    </button>
                    <button
                      className="btn-icon"
                      onClick={() => onDelete(client.id)}
                      title="Delete"
                    >
                      🗑️
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {portalModalClient && (
          <PortalAccessModal
            client={portalModalClient}
            onClose={() => setPortalModalClient(null)}
          />
        )}
      </div>
    );
  }

  return (
    <div className="client-grid">
      {clients.map(client => (
        <div key={client.id} className="client-card">
          <div className="client-card-header">
            <div>
              <h3>{client.name}</h3>
              {client.company && <p className="client-company">{client.company}</p>}
            </div>
            <div className="client-actions">
              <button
                className="btn-icon"
                onClick={() => setPortalModalClient(client)}
                title="Portal Access"
              >
                🌐
              </button>
              <button
                className="btn-icon"
                onClick={() => onEdit(client)}
                title="Edit"
              >
                ✏️
              </button>
              <button
                className="btn-icon"
                onClick={() => onDelete(client.id)}
                title="Delete"
              >
                🗑️
              </button>
            </div>
          </div>

          <div className="client-card-body">
            <div className="client-info">
              <span className="client-label">Email:</span>
              <span>{client.email}</span>
            </div>
            {client.phone && (
              <div className="client-info">
                <span className="client-label">Phone:</span>
                <span>{client.phone}</span>
              </div>
            )}
            <div className="client-info">
              <span className="client-label">Default Rate:</span>
              <span className="client-rate">
                {formatCurrency(client.defaultRate, client.currency)}/hr
              </span>
            </div>
            {client.notes && (
              <div className="client-notes">
                {client.notes}
              </div>
            )}
          </div>
        </div>
      ))}
      {portalModalClient && (
        <PortalAccessModal
          client={portalModalClient}
          onClose={() => setPortalModalClient(null)}
        />
      )}
    </div>
  );
};

export default ClientList;
